import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Job, WebSocketMessagePayload } from "../../hooks/WebSocketProvider";
import {
  createPlanViewStateKey,
  deserializeKey,
  serializeKey,
} from "./stateKeyUtils";
import { hydrateFullPlanStateCases } from "./actions";
import { decodeJobId } from "./helpers";
import labels from "../../utils/labels";
import { PlanViewKey } from "../../hooks/useQueryParams";

export const SolutionTypes = {
  NEXUS: "NexusSolution",
  PONDOO: "PondooSolution",
  CUSTOM: "CustomSolution",
  CONTINGENCY: "ContingencySolution",
} as const;

export type SolutionType = (typeof SolutionTypes)[keyof typeof SolutionTypes];

export type PlanViewStateEntryStatus =
  | "missing"
  | "loading"
  | "ready"
  | "error";

export type PlanViewStateEntry = {
  status: PlanViewStateEntryStatus;
  idScenario?: number;
  idSolution?: number;
  lastMessage?: WebSocketMessagePayload;
  errorMessage?: string;
  job?: Job;
  /**
   * jobRef of the run this cell currently follows. Messages from any other run are ignored, because a newer landing
   * replaces the previous run and the replaced run's late messages must not overwrite the cell.
   */
  activeJobRef?: string;
};

/**
 * This type definition is a way to enforce a kind of type safety on the keys of the state, e.g. any key with a
 * valid PlanViewStateEntry value must be the return type of serializeKey, otherwise it is assigned the `never` type.
 * And since `never` is not assignable to type `PlanViewStateEntry`, those will be invalid keys.
 */
export type PlanViewState = {
  [K in string]: PlanViewStateEntry;
} & {
  [K in string]: K extends ReturnType<typeof serializeKey>
    ? PlanViewStateEntry
    : never;
};

const initialState: PlanViewState = {};

const getKeyForOtherSolutionType = (
  solutionType: SolutionType,
  filter: string,
  date: string,
) => {
  const otherType =
    solutionType === "PondooSolution" ? "NexusSolution" : "PondooSolution";
  return serializeKey(
    createPlanViewStateKey(otherType, parseInt(filter), date),
  );
};

const RUN_START_MESSAGE_TYPES: WebSocketMessagePayload["bridgeMessageType"][] =
  ["NexusLandingInitiatedMessage", "NexusLandingProgressOptimizationInitiated"];

// Messages that are not part of a run's lifecycle, so the active jobRef does not apply to them.
const NON_RUN_MESSAGE_TYPES: WebSocketMessagePayload["bridgeMessageType"][] = [
  "OptimizationStoppedToastMessage",
  "DragAndDropPlanSaved",
];

const setActiveJobRef = (
  state: PlanViewState,
  keys: string[],
  jobRef: string,
) => {
  keys.forEach((key) => {
    const entry = state[key] ?? { status: "loading" };
    state[key] = {
      ...entry,
      activeJobRef: jobRef,
      // A new run has no job until the solver starts, so the old run's job must not drive the progress bar
      job: entry.activeJobRef === jobRef ? entry.job : undefined,
    };
  });
};

// A run that ends without a new plan leaves the cell with the plan it had before the run, if any
const statusBeforeRun = (entry: PlanViewStateEntry): PlanViewStateEntryStatus =>
  entry.idSolution || entry.idScenario ? "ready" : "missing";

const realTimeUpdateSlice = createSlice({
  name: "real-time-updates",
  initialState: initialState,
  reducers: {
    handleWebsocketMessage: (
      state,
      action: PayloadAction<WebSocketMessagePayload>,
    ) => {
      const message = action.payload;

      const { filter, date } = decodeJobId(message.jobId);

      const solutionType: SolutionType = message.bridgeMessageType
        .toLowerCase()
        .includes("optimization")
        ? "PondooSolution"
        : "NexusSolution";
      const key = serializeKey(
        createPlanViewStateKey(solutionType, parseInt(filter), date),
      );
      const otherKey = getKeyForOtherSolutionType(solutionType, filter, date);

      if (!(key in state)) state[key] = { status: "loading" };

      if (RUN_START_MESSAGE_TYPES.includes(message.bridgeMessageType)) {
        setActiveJobRef(state, [key, otherKey], message.jobId);
      } else if (!NON_RUN_MESSAGE_TYPES.includes(message.bridgeMessageType)) {
        const activeJobRef = state[key].activeJobRef;
        if (activeJobRef && activeJobRef !== message.jobId) return;
        // No run known yet (e.g. after hydration without messages, or a missed start message): adopt this one,
        // for both views, since a run always covers the whole cell
        if (!activeJobRef) {
          state[key].activeJobRef = message.jobId;
          if (state[otherKey] && !state[otherKey].activeJobRef)
            state[otherKey].activeJobRef = message.jobId;
        }
      }

      switch (message.bridgeMessageType) {
        case "NexusLandingInitiatedMessage":
          // The landing replaces both plans, so neither may offer "Opdater" on the old scenario while it fetches
          state[key].status = "loading";
          state[otherKey].status = "loading";
          break;
        case "NexusLandingSupersededMessage":
          // Not an error: a newer run took over the cell, so follow it.
          setActiveJobRef(
            state,
            [key, otherKey],
            message.data.supersededByJobRef,
          );
          state[key].status = "loading";
          state[otherKey].status = "loading";
          break;
        case "NexusLandingProgressDataRetrievalInitiatedMessage":
          state[key].status = "loading";
          break;
        case "NexusLandingDataRetrievalAlreadyRunningMessage":
          state[key].status = "loading";
          break;
        case "NexusLandingProgressNexusDataReadyMessage":
          state[key] = {
            ...state[key],
            status: "ready",
            idScenario: message.data.idScenario,
          };
          break;
        case "NexusLandingProgressDataRetrievalFailedMessage":
          state[key].status = "error";
          state[key].errorMessage = message.data;
          // No optimization follows a failed fetch: the previous Pondoo plan (if any) is still the cell's plan
          state[otherKey].status = statusBeforeRun(state[otherKey]);
          break;
        case "NexusLandingProgressOptimizationInitiated":
          state[key] = {
            ...state[key],
            status: "loading",
            idScenario: message.data.idScenario,
            job: message.data,
          };
          // In this case we also need to update the alternate key's job
          state[otherKey] = { ...state[otherKey], job: message.data };
          break;
        case "NexusLandingProgressOptimizationFailed":
          state[key] = {
            ...state[key],
            status: "error",
            job: message.data as Job,
            errorMessage: labels.pondooStatusError,
          };
          break;
        case "NexusLandingProgressOptimizationAborted":
          state[key] = {
            ...state[key],
            status: "error",
            job: message.data as Job,
            errorMessage: labels.pondooStatusError,
          };
          state[otherKey] = { ...state[otherKey], job: message.data };
          // Stopped while fetching from Nexus: the previous Nexus plan (if any) is still the cell's plan
          if (state[otherKey].status === "loading")
            state[otherKey].status = statusBeforeRun(state[otherKey]);
          break;
        case "NexusLandingProgressOptimizationFinished":
          state[key] = {
            ...state[key],
            status: "ready",
            idScenario: message.data.idScenario,
            job: message.data as Job,
          };
          state[otherKey] = { ...state[otherKey], job: message.data };
          break;
      }
    },
    resetPondooViewStateByScenarioId: (
      state,
      action: PayloadAction<number>,
    ) => {
      const entry = Object.entries(state).find(
        (kv) =>
          kv[1].idScenario === action.payload &&
          deserializeKey(kv[0]).solutionType === "PondooSolution",
      );
      if (!entry) {
        console.error(
          "Could not find PondooSolution with idScenario: ",
          action.payload,
        );
        return;
      }

      state[entry[0]] = { status: "loading" };
    },
    resetPondooViewState: (state, action: PayloadAction<PlanViewKey>) => {
      state[serializeKey(action.payload)] = { status: "missing" };
    },
    setPlanViewStateLoading: (state, action: PayloadAction<PlanViewKey>) => {
      state[serializeKey(action.payload)] = { status: "loading" };
    },
    setViewStateError: (
      state,
      action: PayloadAction<{ key: PlanViewKey; errorMessage: string }>,
    ) => {
      const { key, errorMessage } = action.payload;
      const serializedKey = serializeKey(key);
      if (!(serializedKey in state)) return;

      state[serializedKey] = {
        ...state[serializedKey],
        status: "error",
        errorMessage: errorMessage,
      };
    },
  },
  extraReducers: (builder) => {
    hydrateFullPlanStateCases(builder);
  },
});

export const {
  handleWebsocketMessage,
  resetPondooViewStateByScenarioId,
  resetPondooViewState,
  setPlanViewStateLoading,
  setViewStateError,
} = realTimeUpdateSlice.actions;
export default realTimeUpdateSlice.reducer;
